/**
 * Centralized chat-related constants and options
 * This file consolidates all option arrays and constants used across ChatsPage components
 */

import { CreateChatType } from '@choco-admin/types';

// Chat Type Options - Centralized definition
export const CHAT_TYPE_OPTIONS = [
  {
    value: CreateChatType.ORDERING,
    label: 'Ordering',
    description: 'Standard ordering chat for product purchases',
  },
  {
    value: CreateChatType.SUPPORT,
    label: 'Support',
    description: 'Support chat for customer assistance',
  },
];

// Currency Options - Centralized definition
export const CURRENCY_OPTIONS = [
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
] as const;

// Chat Status Options
export const CHAT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'pending', label: 'Pending' },
  { value: 'requested', label: 'Requested' },
] as const;

// Filter Options
export const CHAT_ENABLED_FILTER_OPTIONS = [
  { value: 'all', label: 'All Chats' },
  { value: 'enabled', label: 'Enabled Only' },
  { value: 'disabled', label: 'Disabled Only' },
] as const;

export const CHAT_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  ...CHAT_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  })),
] as const;

// Form Field Labels - Centralized for consistency
export const FORM_LABELS = {
  chatName: 'Chat Name',
  restaurantName: 'Restaurant Name',
  deliveryAddress: 'Delivery Address',
  additionalEmails: 'Additional Emails',
  customerNumber: 'Customer Number',
  chatType: 'Chat Type',
  subCatalog: 'Sub Catalog',
  visibleChatPrices: 'Show Prices in Chat',
  isPriceOverrideEnabled: 'Enable Price Override',
  customerLevelMovAmount: 'MOV Amount',
  customerLevelMovCurrency: 'MOV Currency',
} as const;

// Form Placeholders
export const FORM_PLACEHOLDERS = {
  chatName: 'Enter chat name...',
  restaurantName: 'Enter restaurant name...',
  deliveryAddress: 'Enter delivery address...',
  additionalEmails: 'example@domain.com, another@domain.com',
  customerNumber: 'Enter customer number...',
  chatType: 'Select chat type...',
  subCatalog: 'Select sub catalog...',
  customerLevelMovAmount: 'Enter minimum order value...',
  customerLevelMovCurrency: 'Select currency...',
} as const;

// Helper functions for options
export const getChatTypeOption = (value: CreateChatType) =>
  CHAT_TYPE_OPTIONS.find((option) => option.value === value);

// Convert ChatType to CreateChatType for form compatibility
export const convertChatTypeToCreateChatType = (
  chatType: string
): CreateChatType | undefined => {
  switch (chatType) {
    case 'ORDERING':
      return CreateChatType.ORDERING;
    case 'SUPPORT':
      return CreateChatType.SUPPORT;
    default:
      return undefined;
  }
};

export const getChatTypeOptionFromChatType = (chatType: string) => {
  const createChatType = convertChatTypeToCreateChatType(chatType);
  return createChatType ? getChatTypeOption(createChatType) : undefined;
};

export const getCurrencyOption = (value: string) =>
  CURRENCY_OPTIONS.find((option) => option.value === value);

export const getChatStatusOption = (value: string) =>
  CHAT_STATUS_OPTIONS.find((option) => option.value === value);
